import { useState } from 'react';

// The export endpoint requires the admin auth header, so it can't be a plain <a download>.
// Fetch it as a blob and hand the browser an object URL instead.
async function downloadExport(apiFetch, params, fallbackName) {
  const res = await apiFetch(`/api/admin/support/tickets/export?${params}`);
  if (!res.ok) {
    let msg = 'Export failed';
    try { msg = (await res.json()).error || msg; } catch (_) {}
    throw new Error(msg);
  }
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = match ? match[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function SupportExportModal({ apiFetch, toast, onClose, categories = [] }) {
  const [scope, setScope] = useState('tickets');
  const [format, setFormat] = useState('csv');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [includeMessages, setIncludeMessages] = useState(false);
  const [busy, setBusy] = useState(false);

  // Nested replies can't be represented in a flat CSV.
  const jsonOnly = scope === 'both';
  const effectiveFormat = jsonOnly ? 'json' : format;
  const messagesRelevant = scope !== 'tickets';

  const run = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ scope, format: effectiveFormat });
      if (status) params.set('status', status);
      if (category) params.set('category', category);
      if (priority) params.set('priority', priority);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (messagesRelevant && includeMessages) params.set('include_messages', '1');
      await downloadExport(apiFetch, params, `support-${scope}.${effectiveFormat}`);
      toast.success('Exported', 'Your download should begin shortly');
      onClose();
    } catch (err) {
      toast.error('Error', err.message || 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  const label = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 };
  const row = { display: 'flex', gap: 12, flexWrap: 'wrap' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
         onClick={onClose}>
      <div className="admin-card" style={{ width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 20 }}
           onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>Export support data</h2>
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={label}>What to export</label>
            <div style={row}>
              {[
                ['tickets', 'Tickets', 'One row per ticket, with response and resolution times'],
                ['replies', 'Replies', 'One row per message in every thread'],
                ['both', 'Tickets + replies', 'Nested JSON — best for tooling'],
              ].map(([v, title, hint]) => (
                <label key={v} style={{ flex: '1 1 150px', border: '1px solid var(--admin-border)', borderRadius: 8, padding: '10px 12px', cursor: 'pointer', background: scope === v ? 'var(--admin-bg)' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                    <input type="radio" checked={scope === v} onChange={() => setScope(v)} />{title}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', marginTop: 4 }}>{hint}</div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label style={label}>Format</label>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, opacity: jsonOnly ? 0.5 : 1 }}>
                <input type="radio" checked={effectiveFormat === 'csv'} disabled={jsonOnly} onChange={() => setFormat('csv')} /> CSV
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                <input type="radio" checked={effectiveFormat === 'json'} onChange={() => setFormat('json')} /> JSON
              </label>
              {jsonOnly && <span style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>Nested replies require JSON</span>}
            </div>
          </div>

          <div>
            <label style={label}>Filters (optional)</label>
            <div style={row}>
              <select className="admin-select" style={{ flex: '1 1 140px' }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">Any status</option>
                {['open', 'in_progress', 'resolved', 'closed'].map(s => <option key={s} value={s}>{s === 'in_progress' ? 'In progress' : s}</option>)}
              </select>
              <select className="admin-select" style={{ flex: '1 1 140px' }} value={priority} onChange={e => setPriority(e.target.value)}>
                <option value="">Any priority</option>
                {['low', 'normal', 'high', 'urgent'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select className="admin-select" style={{ flex: '1 1 140px' }} value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">Any category</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ ...row, marginTop: 10 }}>
              <div style={{ flex: '1 1 140px' }}>
                <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', marginBottom: 3 }}>Created from</div>
                <input className="admin-input" type="date" style={{ width: '100%' }} value={from} onChange={e => setFrom(e.target.value)} />
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', marginBottom: 3 }}>Created to</div>
                <input className="admin-input" type="date" style={{ width: '100%' }} value={to} onChange={e => setTo(e.target.value)} />
              </div>
            </div>
          </div>

          {messagesRelevant && (
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, border: '1px solid var(--admin-border)', borderRadius: 8, padding: '10px 12px' }}>
              <input type="checkbox" checked={includeMessages} onChange={e => setIncludeMessages(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                Include full message bodies
                <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', marginTop: 3 }}>
                  Off by default. The file will contain customer conversation text. Message counts and lengths are always included.
                </div>
              </span>
            </label>
          )}

          <div style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>
            <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} />
            Exports are recorded in the audit log, including whether message bodies were included.
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={onClose}>Cancel</button>
            <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={run} disabled={busy}>
              {busy ? 'Preparing…' : <><i className="fa-solid fa-download" style={{ marginRight: 6 }} />Download</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupportExportModal;
